import React, { useState, useRef, useEffect } from "react"
import { useMoney } from "../../hooks/useMoney"
import { useAppDispatch, useAppSelector } from "../../store/hook"
import {
  createProgram,
  fetchProgramList,
  CreateProgramPayload,
  Bank,
  BankDetails,
} from "../../features/program/ProgramSlice"
import { Principal } from "../../features/principal/PrincipalSlice"
import { fetchPurposeCodes, fetchPurposeCodeByCode } from "../../features/purposeCode/PurposeCodeSlice"
import { X } from "lucide-react"
import { Processor } from "../../features/program/ProgramSlice"
import { EncryptionUtil } from "../../utils/encryption"
import { validateBankDetails, renderBankDetailsSection, getProcessorBrand } from "./BankDetailsSections"
import { toIciciCbdcLocationPayload } from "../../data/locationCodes"

/* --------------------------------- Helper Functions ------------------------------ */

// Function to determine if a processor requires bank & redemption details
const requiresBankDetails = (processorType: string) => {
  const brand = getProcessorBrand(processorType)
  return brand === 'ICICI' || brand === 'YESBANK' || brand === 'ICICI_CBDC' || brand === 'MYP'
}

// The four bank-integrated brands (ICICI, YESBANK, ICICI_CBDC, MYP) fork on
// issuance lifecycle as a real runtime choice (direct vs indirect) - for
// MYP this is VMS's own issuanceType and nothing else; MYP has no separate
// activation concept of its own (it's always told activationMode: DIRECT
// server-side - see programs/src/services/program.service.js#buildMypProgramFields).
// BLOCKCHAIN has a fixed lifecycle (always indirect - it never calls a real
// bank) rather than a per-program choice, but still needs the field
// visible/correct here, unlike SELP (always direct) which has no lifecycle
// concept worth surfacing at all.
const brandHasIssuanceType = (processorType: string) => {
  const brand = getProcessorBrand(processorType)
  return requiresBankDetails(processorType) || brand === 'BLOCKCHAIN'
}

const isIciciCbdcProcessor = (processorType: string) => {
  return getProcessorBrand(processorType) === 'ICICI_CBDC'
}


/* --------------------------------- Interfaces ------------------------------ */

interface FormErrors extends Record<string, string | undefined> {
  programCode?: string
  programName?: string
  displayName?: string
  description?: string
  logo?: string
  brandImage?: string
  voucherImage?: string
  currency?: string
  processorType?: string
  issuanceType?: string
  tagline?: string
  callToAction?: string
  brandImageAlt?: string
  maximumAmount?: string
  minimumAmount?: string
  issuerBankId?: string
  redemptionType?: string
  merchantId?: string
  subMerchantId?: string
  period?: string
  bankDetailsMcc?: string
  bankDetailsPayerVa?: string
  bankDetailsPurposeCode?: string
  bankDetailsMandateType?: string
  bankDetailsAccessKey?: string
  bankDetailsPayeeVpa?: string
  // YESBANK specific error fields
  bankDetailsMandateName?: string
  bankDetailsAmountRule?: string
  bankDetailsVoucherCategory?: string
  bankDetailsRecurrencePattern?: string
  bankDetailsPayerRevocable?: string
  bankDetailsClientIdentifier?: string
  bankDetailsPreAuth?: string
  bankDetailsRefurl?: string
  cardDescription?: string
  receiverInstructions?: string
  receiverHowToUseInstructions?: string
  standardTerms?: string
  specificTerms?: string
  status?: string
  denominationType?: string
  denominationMaxAmount?: string
  denominationMinAmount?: string
  denominationAmount?: string
}

interface AddProgramsProps {
  principal: Principal
  showComponent: (show: boolean) => void
  programError?: string
  bankList: Bank[],
  processorList: Processor[]
}

interface PurposeCodeOption {
  code: string
  description: string
  mccs: Record<string, string>
}

const AddPrograms: React.FC<AddProgramsProps> = ({
  principal,
  showComponent,
  programError,
  bankList,
  processorList
}) => {
  /* --------------------------------- Redux ------------------------------ */

  const dispatch = useAppDispatch()
  const { code: lobCurrencyCode } = useMoney()
  const { purposeCodes, selectedPurposeCode: selectedPurposeCodeFromStore } = useAppSelector(state => state.purposeCode)

  /* --------------------------------- States ------------------------------ */

  const [formData, setFormData] = useState<CreateProgramPayload>(() => {
    // Only load saved data if there's a programError
    if (programError) {
      const savedData = localStorage.getItem("programFormData")
      if (savedData) {
        try {
          const parsedData = JSON.parse(savedData)
          return {
            ...parsedData,
            logo: null,
            brandImage: null,
            voucherImage: null,
          }
        } catch (e) {
          console.error("Error parsing saved form data:", e)
        }
      }
    }

    // Return default form data if no saved data or error parsing
    return {
      principalId: 0,
      programName: "",
      programCode: "",
      description: "",
      standardTerms: "",
      specificTerms: "",
      displayName: "",
      currency: lobCurrencyCode,
      processorType: "",
      processorTypeDisplay: "",
      issuanceType: "",
      tagline: "",
      cardDescription: "",
      receiverInstructions: "",
      receiverHowToUseInstructions: "",
      callToAction: "",
      brandImageAlt: "",
      maximumAmount: 1,
      minimumAmount: 1,
      issuerBankId: "",
      redemptionType: "",
      merchantId: "",
      subMerchantId: "",
      period: 1,
      bankDetails: {
        mcc: "",
        payerVa: "",
        purposeCode: "",
        mandateType: "",
        accessKey: "",
        payeeVpa: "",
        // YESBANK specific fields
        mandateName: "",
        amountRule: "",
        voucherCategory: "",
        recurrencePattern: "",
        payerRevocable: undefined,
        clientIdentifier: "",
        preAuth: undefined,
        refurl: "",
        // ICICI CBDC specific fields
        sponsorId: "",
        sponsorName: "",
        purpose: "",
        language: "",
        location: [],
        amount: "",
      },
      status: "active",
      denominationType: "",
      denominationAmount: [],
      denominationMaxAmount: "",
      denominationMinAmount: "",
      denominationCurrency: lobCurrencyCode,
    }
  })

  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const [showModal, setShowModal] = useState(false)
  const [showAccessKey, setShowAccessKey] = useState(false)

  const [purposeCodeList, setPurposeCodeList] = useState<PurposeCodeOption[]>([])
  // Tracks which purpose code's MCCs are currently being fetched, for the
  // ICICI CBDC MCC dropdown's lazy per-code loading indicator.
  const [loadingMccsForPurposeCode, setLoadingMccsForPurposeCode] = useState("")

  const selectedPurposeCode = purposeCodeList.find(
    p => p.code === formData.bankDetails.purposeCode,
  )

  const [mccList, setMccList] = useState<
    Array<{ mcc: string; description: string }>
  >(
    selectedPurposeCode?.mccs
      ? Object.keys(selectedPurposeCode.mccs).map(mcc => {
        return { mcc: mcc, description: selectedPurposeCode.mccs[mcc] }
      })
      : [],
  )

  /* ------------------------------ Hooks ---------------------------- */

  // Save form data before component unmounts
  useEffect(() => {
    return () => {
      const dataToSave = {
        ...formData,
        logo: null,
        brandImage: null,
        voucherImage: null,
      }
      localStorage.setItem("programFormData", JSON.stringify(dataToSave))
    }
  }, [formData])

  useEffect(() => {
    dispatch(fetchPurposeCodes())
  }, [dispatch])

  useEffect(() => {
    setPurposeCodeList(
      purposeCodes.map(code => ({
        code: code.purposeCode,
        description: code.useCase,
        mccs: code.mccs || {},
      })),
    )
  }, [purposeCodes])

  // When a specific purpose code is fetched with its MCCs, sync both the
  // single-select mccList (used by ICICI/YESBank) and the purposeCodeList
  // entry's mccs map (used by the ICICI CBDC MCC dropdown, so the code's
  // MCCs actually show up once expanded).
  useEffect(() => {
    if (!selectedPurposeCodeFromStore || !selectedPurposeCodeFromStore.mccs) {
      return
    }

    const mccs = selectedPurposeCodeFromStore.mccs || {}
    const fetchedCode = selectedPurposeCodeFromStore.purposeCode

    if (fetchedCode === formData.bankDetails.purposeCode) {
      setMccList(
        Object.keys(mccs).map(mcc => ({
          mcc,
          description: mccs[mcc] || "",
        })),
      )
    }

    setPurposeCodeList(prev =>
      prev.map(pc => (pc.code === fetchedCode ? { ...pc, mccs } : pc)),
    )
    setLoadingMccsForPurposeCode(prev => (prev === fetchedCode ? "" : prev))
  }, [selectedPurposeCodeFromStore, formData.bankDetails.purposeCode])

  /* ------------------------------ Event Handlers ---------------------------- */

  const handlePurposeCodeChange = (purposeCode: string) => {
    const selectedPurpose = purposeCodeList.find(p => p.code === purposeCode)
    if (selectedPurpose) {
      const { bankDetailsPurposeCode, ...rest } = formErrors
      setFormErrors(rest)

      // Clear current MCC selection and trigger fetch of MCC mappings for this purpose code
      setMccList([])
      setFormData({
        ...formData,
        bankDetails: { ...formData.bankDetails, purposeCode: purposeCode, mcc: "" },
      })

      dispatch(fetchPurposeCodeByCode(purposeCode))
    }
  }

  // ICICI CBDC MCC dropdown: lazily fetch a purpose code's MCCs the first
  // time it's expanded (the purpose code list endpoint doesn't include them).
  const handleExpandPurposeCode = (code: string) => {
    setLoadingMccsForPurposeCode(code)
    dispatch(fetchPurposeCodeByCode(code))
  }

  const readFileAsUrl = (file: File | string): string => {
    if (typeof file === "string") {
      return ""
    }
    return URL.createObjectURL(file)
  }

  const validateData = () => {
    let hasError = false
    const errors: Record<string, string> = {}

    if (!formData.programCode) {
      hasError = true
      errors.programCode = "Please enter a valid program code"
    } else if (
      getProcessorBrand(formData.processorTypeDisplay) === 'YESBANK' &&
      formData.programCode.length !== 7
    ) {
      hasError = true
      errors.programCode = "Program code must be exactly 7 characters for YesBank programs"
    }

    if (!formData.programName) {
      hasError = true
      errors.programName = "Please enter a valid program name"
    }

    if (!formData.displayName) {
      hasError = true
      errors.displayName = "Please enter a valid display name"
    }

    if (!formData.description) {
      hasError = true
      errors.description = "Please enter a valid description"
    }

    if (!formData.logo) {
      hasError = true
      errors.logo = "Please upload a valid logo file"
    }

    if (!formData.brandImage) {
      hasError = true
      errors.brandImage = "Please upload a valid brand image file"
    }

    if (!formData.voucherImage) {
      hasError = true
      errors.voucherImage = "Please upload a valid voucher image file"
    }

    if (!formData.currency) {
      hasError = true
      errors.currency = "Please select a currency"
    }

    if (!formData.processorType) {
      hasError = true
      errors.processorType = "Please select a processor type"
    }

    if (brandHasIssuanceType(formData.processorTypeDisplay) && !formData.issuanceType) {
      hasError = true
      errors.issuanceType = "Please select an issuance type"
    }

    if (!formData.tagline) {
      hasError = true
      errors.tagline = "Please enter a tag line"
    }

    if (!formData.callToAction) {
      hasError = true
      errors.callToAction = "Please enter a call to action"
    }

    if (!formData.brandImageAlt) {
      hasError = true
      errors.brandImageAlt = "Please enter a brand image alt"
    }

    if (!formData.cardDescription) {
      hasError = true
      errors.cardDescription = "Please enter a card description"
    }

    if (!formData.receiverInstructions) {
      hasError = true
      errors.receiverInstructions = "Please enter receiver instructions"
    }

    if (!formData.receiverHowToUseInstructions) {
      hasError = true
      errors.receiverHowToUseInstructions = "Please enter a valid receiver how to use instructions"
    }

    if (!formData.standardTerms) {
      hasError = true
      errors.standardTerms = "Please enter a valid standard terms"
    }

    if (!formData.maximumAmount) {
      hasError = true
      errors.maximumAmount = "Please enter a valid maximum amount"
    }

    if (!formData.minimumAmount) {
      hasError = true
      errors.minimumAmount = "Please enter a valid minimum amount"
    }

    if (!formData.denominationType) {
      hasError = true
      errors.denominationType = "Please select a denomination type"
    }

    if (formData.denominationType === "fixed") {
      if (!formData.denominationAmount || formData.denominationAmount.length === 0) {
        hasError = true
        errors.denominationAmount = "Please enter at least one denomination amount"
      }
    } else {
      if (!formData.denominationMaxAmount) {
        hasError = true
        errors.denominationMaxAmount = "Please enter a valid denomination maximum amount"
      }

      if (!formData.denominationMinAmount) {
        hasError = true
        errors.denominationMinAmount = "Please enter a valid denomination minimum amount"
      }

    }

    // Validate Bank & Redemption Details if processor requires them
    if (requiresBankDetails(formData.processorTypeDisplay)) {

      // Validate bank details using the imported validation function
      const bankDetailsHasError = validateBankDetails(formData.processorTypeDisplay, formData, errors)
      if (bankDetailsHasError) {
        hasError = true
      }
    }
    return { hasError, errors }
  }

  const handleAddProgram = async (programData: CreateProgramPayload) => {

    const formDataToSend = new FormData()
    formDataToSend.append("principalId", String(programData.principalId))
    formDataToSend.append("programName", programData.programName)
    formDataToSend.append("programCode", programData.programCode)
    formDataToSend.append("description", programData.description)
    formDataToSend.append("standardTerms", programData.standardTerms)
    formDataToSend.append("specificTerms", programData.specificTerms)
    formDataToSend.append("displayName", programData.displayName)
    formDataToSend.append("currency", programData.currency)
    formDataToSend.append("processorType", programData.processorType)
    if (programData.issuanceType) {
      formDataToSend.append("issuanceType", programData.issuanceType)
    }
    formDataToSend.append("tagline", programData.tagline)
    formDataToSend.append("cardDescription", programData.cardDescription)
    formDataToSend.append(
      "receiverInstructions",
      programData.receiverInstructions,
    )
    formDataToSend.append(
      "receiverHowToUseInstructions",
      programData.receiverHowToUseInstructions,
    )
    formDataToSend.append("callToAction", programData.callToAction)
    formDataToSend.append("brandImageAlt", programData.brandImageAlt)
    formDataToSend.append("maximumAmount", String(programData.maximumAmount))
    formDataToSend.append("minimumAmount", String(programData.minimumAmount))

    // Default values
    formDataToSend.append("status", "active")

    formDataToSend.append("denominationType", programData.denominationType || "")
    if (programData.denominationType === "fixed") {
      formDataToSend.append("denominationMaxAmount", "")
      formDataToSend.append("denominationMinAmount", "")
      formDataToSend.append("denominationAmount", JSON.stringify(programData.denominationAmount))
    }
    else {
      formDataToSend.append("denominationMaxAmount", String(programData.denominationMaxAmount))
      formDataToSend.append("denominationMinAmount", String(programData.denominationMinAmount))
      formDataToSend.append("denominationAmount", JSON.stringify([0]))
    }


    if (programData.logo) formDataToSend.append("logo", programData.logo)
    if (programData.brandImage)
      formDataToSend.append("brandImage", programData.brandImage)
    if (programData.voucherImage)
      formDataToSend.append("voucherImage", programData.voucherImage)

    // Send bank-related fields based on processor requirements
    if (requiresBankDetails(programData.processorTypeDisplay)) {

      formDataToSend.append("period", String(programData.period));

      // Clean up bank details based on processor type
      const cleanBankDetails: BankDetails = {}

      if (getProcessorBrand(programData.processorTypeDisplay) === 'ICICI') {
        formDataToSend.append("merchantId", programData.merchantId);
        formDataToSend.append("subMerchantId", programData.subMerchantId);
        formDataToSend.append("redemptionType", programData.redemptionType);

        cleanBankDetails.mcc = programData.bankDetails.mcc
        cleanBankDetails.payerVa = programData.bankDetails.payerVa
        cleanBankDetails.purposeCode = programData.bankDetails.purposeCode
        cleanBankDetails.mandateType = programData.bankDetails.mandateType
        cleanBankDetails.accessKey = programData.bankDetails.accessKey ?
          await EncryptionUtil.encrypt(programData.bankDetails.accessKey, window.env.ICICI_INTERNAL_API_KEY) : ""

        if (programData.bankDetails.mandateType === "04") {
          cleanBankDetails.payeeVpa = programData.bankDetails.payeeVpa || ""
        }
      }
      else if (getProcessorBrand(programData.processorTypeDisplay) === 'YESBANK') {
        cleanBankDetails.purposeCode = programData.bankDetails.purposeCode
        cleanBankDetails.mandateName = programData.bankDetails.mandateName
        cleanBankDetails.amountRule = programData.bankDetails.amountRule
        cleanBankDetails.voucherCategory = programData.bankDetails.voucherCategory
        cleanBankDetails.recurrencePattern = programData.bankDetails.recurrencePattern
        cleanBankDetails.payerRevocable = programData.bankDetails.payerRevocable
        cleanBankDetails.preAuth = programData.bankDetails.preAuth
        if (programData.bankDetails.clientIdentifier) {
          cleanBankDetails.clientIdentifier = programData.bankDetails.clientIdentifier
        }
        if (programData.bankDetails.refurl) {
          cleanBankDetails.refurl = programData.bankDetails.refurl
        }
      }
      else if (isIciciCbdcProcessor(programData.processorTypeDisplay)) {
        // ICICI CBDC: sponsor-details, purpose, mcc, language, location are
        // stored as plain bank details, same as any other processor's
        // bankDetails - program creation no longer calls any ICICI CBDC
        // bank API. Location rows are stored in the form as human-readable
        // state/city names for a usable UI; they're converted to the
        // bank's numeric {city,state,bufferZone} codes here, right before
        // submission.
        cleanBankDetails.sponsorId = programData.bankDetails.sponsorId
        cleanBankDetails.sponsorName = programData.bankDetails.sponsorName
        cleanBankDetails.purpose = programData.bankDetails.purpose
        // Comma-separated string (e.g. "5472" or "3421, 5123"), not an array.
        cleanBankDetails.mcc = programData.bankDetails.mcc
        if (programData.bankDetails.language) {
          cleanBankDetails.language = programData.bankDetails.language
        }
        if (programData.bankDetails.location && programData.bankDetails.location.length > 0) {
          cleanBankDetails.location = toIciciCbdcLocationPayload(programData.bankDetails.location)
        }
      }
      else if (getProcessorBrand(programData.processorTypeDisplay) === 'MYP') {
        // limits/merchantRules stay as the raw comma-separated-string /
        // bare-number-string shapes the form collects them in - normalized
        // into MYP's expected arrays/"X.XX" strings server-side, in
        // program.service.js#provisionMypProgram.
        cleanBankDetails.programType = programData.bankDetails.programType || "LONG_RUNNING"
        cleanBankDetails.expiry = programData.bankDetails.expiry
        cleanBankDetails.limits = programData.bankDetails.limits
        if (programData.bankDetails.merchantRules?.mode === 'RESTRICTED') {
          cleanBankDetails.merchantRules = programData.bankDetails.merchantRules
        }
        if (programData.bankDetails.lowFloatThreshold) {
          cleanBankDetails.lowFloatThreshold = programData.bankDetails.lowFloatThreshold
        }
      }
      formDataToSend.append("bankDetails", JSON.stringify(cleanBankDetails))
    }
    try {
      const response = await dispatch(createProgram(formDataToSend))
      if (createProgram.fulfilled.match(response)) {
        localStorage.removeItem("programFormData") // Clear saved data on successful submission
        showComponent(false)
      }
    } catch (error) {
      console.error("Error creating program:", error)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const { hasError, errors } = validateData()
    if (hasError) {
      setFormErrors(errors)

      const firstErrorField = Object.keys(errors)[0]

      // Map error keys to input IDs
      const getInputId = (errorKey: string) => {
        const capitalizedErrorKey = errorKey.charAt(0).toUpperCase() + errorKey.slice(1);
        if (capitalizedErrorKey.startsWith("BankDetails")) {
          return "add" + capitalizedErrorKey.replace("BankDetails", "")
        }
        return "add" + capitalizedErrorKey
      }


      const errorElement = document.getElementById(getInputId(firstErrorField))
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" })
        errorElement.focus()
      }

      // Save form data when validation fails
      const dataToSave = {
        ...formData,
        logo: null,
        brandImage: null,
        voucherImage: null,
      }
      localStorage.setItem("programFormData", JSON.stringify(dataToSave))
      return
    }

    handleAddProgram({
      ...formData,
      principalId: Number(principal.principalId),
    })
  }

  /* ------------------------------ UI Handlers ---------------------------- */
  return (
    <div className="max-w-7xl mx-auto px-8 pb-8">
      <form onSubmit={handleSubmit} className="space-y-8">
        {programError && (
          <div
            id="add_ProgramError"
            className="p-4 mb-6 text-sm text-red-700 bg-red-100 rounded-lg"
            role="alert"
          >
            {programError}
          </div>
        )}

        {/* Basic Details Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Basic Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="addProgramName" className="block text-sm font-medium text-gray-700">
                Program Name
              </label>
              <input
                type="text"
                id="addProgramName"
                placeholder="Enter program name"
                value={formData.programName}
                onChange={e => {
                  const { programName, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, programName: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.programName ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.programName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.programName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addDisplayName" className="block text-sm font-medium text-gray-700">
                Display Name
              </label>
              <input
                type="text"
                id="addDisplayName"
                placeholder="Enter display name"
                value={formData.displayName}
                onChange={e => {
                  const { displayName, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, displayName: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.displayName ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.displayName && (
                <p className="mt-1 text-sm text-red-600">{formErrors.displayName}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addProcessorType" className="block text-sm font-medium text-gray-700">
                Processor Type
              </label>
              <div className="relative">
                <select
                  id="addProcessorType"
                  value={formData.processorType}
                  onChange={e => {
                    const selectedProcessor = processorList.find(p => p.processorId.toString() === e.target.value)
                    const newProcessorTypeDisplay = selectedProcessor?.processorType || ""
                    const { processorType, issuanceType, ...rest } = formErrors
                    setFormErrors(rest)
                    setFormData({
                      ...formData,
                      processorType: e.target.value,
                      processorTypeDisplay: newProcessorTypeDisplay,
                      // Changing brand invalidates any previously chosen
                      // issuance lifecycle - force the user to re-pick it.
                      issuanceType: "",
                      // MCC is a single string for ICICI/YESBank but a multi-select
                      // array for ICICI CBDC - reset it to the right shape when the
                      // processor type changes so stale data isn't submitted.
                      bankDetails: {
                        ...formData.bankDetails,
                        mcc: "",
                      },
                    })
                  }}
                  className={`block w-full px-3 py-2 border ${formErrors.processorType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700  focus:outline-none appearance-none transition`}
                >
                  <option value="">Select Processor Type</option>
                  {processorList.map(processor => (
                    <option key={processor.processorType} value={processor.processorId}>
                      {processor.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {formErrors.processorType && (
                <p className="mt-1 text-sm text-red-600">{formErrors.processorType}</p>
              )}
            </div>

            {brandHasIssuanceType(formData.processorTypeDisplay) && (
              <div className="space-y-2">
                <label htmlFor="addIssuanceType" className="block text-sm font-medium text-gray-700">
                  Issuance Type
                </label>
                <div className="relative">
                  <select
                    id="addIssuanceType"
                    value={formData.issuanceType}
                    onChange={e => {
                      const { issuanceType, ...rest } = formErrors
                      setFormErrors(rest)
                      setFormData({ ...formData, issuanceType: e.target.value as typeof formData.issuanceType })
                    }}
                    className={`block w-full px-3 py-2 border ${formErrors.issuanceType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700 focus:outline-none appearance-none transition`}
                  >
                    <option value="">Select issuance type</option>
                    <option value="direct">Direct</option>
                    <option value="indirect">Indirect</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {formErrors.issuanceType && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.issuanceType}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="addProgramCode" className="block text-sm font-medium text-gray-700">
                Program Code
                {['YESBANK_DIRECT', 'YESBANK_INDIRECT'].includes(formData.processorTypeDisplay) && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">(exactly 7 characters)</span>
                )}
              </label>
              <input
                type="text"
                id="addProgramCode"
                placeholder="Enter program code"
                maxLength={['YESBANK_DIRECT', 'YESBANK_INDIRECT'].includes(formData.processorTypeDisplay) ? 7 : undefined}
                value={formData.programCode}
                onChange={e => {
                  const { programCode, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, programCode: e.target.value.toUpperCase() })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.programCode ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.programCode && (
                <p className="mt-1 text-sm text-red-600">{formErrors.programCode}</p>
              )}
            </div>



            <div className="space-y-2">
              <label htmlFor="addCurrency" className="block text-sm font-medium text-gray-700">
                Currency
              </label>
              <select
                id="addCurrency"
                value={formData.currency}
                onChange={e => {
                  setFormData({ ...formData, currency: e.target.value })
                  const { currency, ...rest } = formErrors
                  setFormErrors(rest)
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.currency ? 'border-red-500' : 'border-gray-300'
                  }`}
              >
                <option value={lobCurrencyCode}>{lobCurrencyCode}</option>
              </select>
              {formErrors.currency && (
                <p className="mt-1 text-sm text-red-600">{formErrors.currency}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addTagline" className="block text-sm font-medium text-gray-700">
                Tag Line
              </label>
              <input
                type="text"
                id="addTagline"
                placeholder="Enter tag line"
                value={formData.tagline}
                onChange={e => {
                  setFormData({ ...formData, tagline: e.target.value })
                  const { tagline, ...rest } = formErrors
                  setFormErrors(rest)
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.tagline ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.tagline && (
                <p className="mt-1 text-sm text-red-600">{formErrors.tagline}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addCallToAction" className="block text-sm font-medium text-gray-700">
                Call to Action
              </label>
              <input
                type="text"
                id="addCallToAction"
                placeholder="Enter call to action"
                value={formData.callToAction}
                onChange={e => {
                  setFormData({ ...formData, callToAction: e.target.value })
                  const { callToAction, ...rest } = formErrors
                  setFormErrors(rest)
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.callToAction ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.callToAction && (
                <p className="mt-1 text-sm text-red-600">{formErrors.callToAction}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addBrandImageAlt" className="block text-sm font-medium text-gray-700">
                Brand Image Alt
              </label>
              <input
                type="text"
                id="addBrandImageAlt"
                placeholder="Enter brand image alt"
                value={formData.brandImageAlt}
                onChange={e => {
                  setFormData({ ...formData, brandImageAlt: e.target.value })
                  const { brandImageAlt, ...rest } = formErrors
                  setFormErrors(rest)
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.brandImageAlt ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.brandImageAlt && (
                <p className="mt-1 text-sm text-red-600">{formErrors.brandImageAlt}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addCardDescription" className="block text-sm font-medium text-gray-700">
                Card Description
              </label>
              <input
                type="text"
                id="addCardDescription"
                placeholder="Enter card description"
                value={formData.cardDescription}
                onChange={e => {
                  setFormData({ ...formData, cardDescription: e.target.value })
                  const { cardDescription, ...rest } = formErrors
                  setFormErrors(rest)
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.cardDescription ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.cardDescription && (
                <p className="mt-1 text-sm text-red-600">{formErrors.cardDescription}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addDescription" className="block text-sm font-medium text-gray-700">
                About
              </label>
              <textarea
                id="addDescription"
                rows={3}
                placeholder="Enter description"
                value={formData.description}
                onChange={e => {
                  setFormErrors({ ...formErrors, description: "" })
                  setFormData({ ...formData, description: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.description && (
                <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addStandardTerms" className="block text-sm font-medium text-gray-700">
                Terms
              </label>
              <textarea
                id="addStandardTerms"
                rows={3}
                placeholder="Enter standard terms"
                value={formData.standardTerms}
                onChange={e => {
                  const { standardTerms, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, standardTerms: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.standardTerms ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.standardTerms && (
                <p className="mt-1 text-sm text-red-600">{formErrors.standardTerms}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addSpecificTerms" className="block text-sm font-medium text-gray-700">
                Additional Terms
              </label>
              <textarea
                id="addSpecificTerms"
                rows={3}
                placeholder="Enter specific terms"
                value={formData.specificTerms}
                onChange={e => {
                  const { specificTerms, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, specificTerms: e.target.value })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.specificTerms ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.specificTerms && (
                <p className="mt-1 text-sm text-red-600">{formErrors.specificTerms}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addMaximumAmount" className="block text-sm font-medium text-gray-700">
                Order Maximum Amount
              </label>
              <input
                type="number"
                id="addMaximumAmount"
                min={1}
                placeholder="Enter maximum amount"
                value={formData.maximumAmount}
                onChange={e => {
                  const { maximumAmount, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, maximumAmount: Number(e.target.value) })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.maximumAmount ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="addMinimumAmount" className="block text-sm font-medium text-gray-700">
                Order Minimum Amount
              </label>
              <input
                type="number"
                id="addMinimumAmount"
                min={1}
                placeholder="Enter minimum amount"
                value={formData.minimumAmount}
                onChange={e => {
                  const { minimumAmount, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, minimumAmount: Number(e.target.value) })
                }}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.minimumAmount ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>

          </div>
        </div>





        {/* Bank & Redemption Details Section */}
        {requiresBankDetails(formData.processorTypeDisplay) && (
          <div className="border rounded-lg p-6 bg-white">
            <h2 className="text-lg font-semibold mb-4">Bank & Redemption Details</h2>
            {renderBankDetailsSection(formData.processorTypeDisplay, {
              formData,
              formErrors: formErrors as Record<string, string>,
              setFormData,
              setFormErrors,
              showAccessKey,
              setShowAccessKey,
              decryptedAccessKey: "", // No decrypted access key for new programs
              mccList,
              purposeCodeList: purposeCodeList.map(p => ({ purposeCode: p.code, description: p.description })),
              purposeCodeOptions: purposeCodeList,
              fieldPrefix: "add",
              handlePurposeCodeChange,
              onExpandPurposeCode: handleExpandPurposeCode,
              loadingMccsForPurposeCode
            })}
          </div>
        )}

        {/* Images Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Logo */}
            <div className="flex flex-col items-start justify-between h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
              <div className="relative w-full h-48 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50 mb-2">
                {formData.logo ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prevState => ({ ...prevState, logo: null }))
                        const fileInput = document.getElementById("addLogo") as HTMLInputElement
                        if (fileInput) fileInput.value = ""
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full  hover:bg-gray-100 z-10"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <img src={readFileAsUrl(formData.logo)} alt="" className="w-full h-full object-contain" />
                  </>
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>
              <input
                type="file"
                id="addLogo"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0]
                  if (file) setFormData({ ...formData, logo: file })
                  setFormErrors({ ...formErrors, logo: "" })
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
              />
              {formErrors.logo && (
                <p className="mt-1 text-sm text-red-600">{formErrors.logo}</p>
              )}
            </div>
            {/* Banner Image */}
            <div className="flex flex-col items-start justify-between h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
              <div className="relative w-full h-48 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50 mb-2">
                {formData.brandImage ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prevState => ({ ...prevState, brandImage: null }))
                        const fileInput = document.getElementById("addBrandImage") as HTMLInputElement
                        if (fileInput) fileInput.value = ""
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full  hover:bg-gray-100 z-10"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <img src={readFileAsUrl(formData.brandImage)} alt="" className="w-full h-full object-cover" />
                  </>
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>
              <input
                type="file"
                id="addBrandImage"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0]
                  if (file) setFormData({ ...formData, brandImage: file })
                  setFormErrors({ ...formErrors, brandImage: "" })
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
              />
              {formErrors.brandImage && (
                <p className="mt-1 text-sm text-red-600">{formErrors.brandImage}</p>
              )}
            </div>
            {/* Voucher Image */}
            <div className="flex flex-col items-start justify-between h-full">
              <label className="block text-sm font-medium text-gray-700 mb-2">Voucher Image</label>
              <div className="relative w-full h-48 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50 mb-2">
                {formData.voucherImage ? (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prevState => ({ ...prevState, voucherImage: null }))
                        const fileInput = document.getElementById("addVoucherImage") as HTMLInputElement
                        if (fileInput) fileInput.value = ""
                      }}
                      className="absolute top-2 right-2 p-1 bg-white rounded-full  hover:bg-gray-100 z-10"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                    <img src={readFileAsUrl(formData.voucherImage)} alt="" className="w-full h-full object-contain" />
                  </>
                ) : (
                  <span className="text-gray-400">No image</span>
                )}
              </div>
              <input
                type="file"
                id="addVoucherImage"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0]
                  if (file) setFormData({ ...formData, voucherImage: file })
                  setFormErrors({ ...formErrors, voucherImage: "" })
                }}
                className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-brand file:text-white hover:file:bg-brand/80"
              />
              {formErrors.voucherImage && (
                <p className="mt-1 text-sm text-red-600">{formErrors.voucherImage}</p>
              )}
            </div>
          </div>
        </div>

        {/* Denomination Details Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Denomination Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="addDenominationType" className="block text-sm font-medium text-gray-700">
                Denomination Type
              </label>
              <div className="relative">
                <select
                  id="addDenominationType"
                  value={formData.denominationType}
                  onChange={e => {
                    const { denominationType, ...rest } = formErrors
                    setFormErrors(rest)
                    setFormData({ ...formData, denominationType: e.target.value })
                  }}
                  className={`block w-full px-3 py-2 border ${formErrors.denominationType ? 'border-red-500' : 'border-gray-300'} rounded-md bg-white text-sm text-gray-700  focus:outline-none appearance-none transition`}
                >
                  <option value="">Select denomination type</option>
                  <option value="fixed">Fixed</option>
                  <option value="range">Range</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {formErrors.denominationType && (
                <p className="mt-1 text-sm text-red-600">{formErrors.denominationType}</p>
              )}
            </div>

            {formData.denominationType === "fixed" && (
              <div className="space-y-2">
                <label htmlFor="addDenominationAmount" className="block text-sm font-medium text-gray-700">
                  Denomination Amount
                </label>
                <input
                  type="number"
                  id="addDenominationAmount"
                  placeholder="Enter denomination amount"
                  value={formData.denominationAmount[0] || ""}
                  onChange={e => {
                    const { denominationAmount, ...rest } = formErrors
                    setFormErrors(rest)
                    setFormData({
                      ...formData,
                      denominationAmount: [parseInt(e.target.value)],
                    })
                  }}
                  className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.denominationAmount ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {formErrors.denominationAmount && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.denominationAmount}</p>
                )}
              </div>
            )}

            {formData.denominationType === "range" && (
              <>
                <div className="space-y-2">
                  <label htmlFor="addDenominationMinAmount" className="block text-sm font-medium text-gray-700">
                    Minimum Amount
                  </label>
                  <input
                    type="number"
                    id="addDenominationMinAmount"
                    placeholder="Enter minimum amount"
                    value={formData.denominationMinAmount || ""}
                    onChange={e => {
                      const { denominationMinAmount, ...rest } = formErrors
                      setFormErrors(rest)
                      setFormData({
                        ...formData,
                        denominationMinAmount: parseInt(e.target.value),
                      })
                    }}
                    className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.denominationMinAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.denominationMinAmount && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.denominationMinAmount}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="addDenominationMaxAmount" className="block text-sm font-medium text-gray-700">
                    Maximum Amount
                  </label>
                  <input
                    type="number"
                    id="addDenominationMaxAmount"
                    placeholder="Enter maximum amount"
                    value={formData.denominationMaxAmount || ""}
                    onChange={e => {
                      const { denominationMaxAmount, ...rest } = formErrors
                      setFormErrors(rest)
                      setFormData({
                        ...formData,
                        denominationMaxAmount: parseInt(e.target.value),
                      })
                    }}
                    className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.denominationMaxAmount ? 'border-red-500' : 'border-gray-300'
                      }`}
                  />
                  {formErrors.denominationMaxAmount && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.denominationMaxAmount}</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Terms & Instructions Section */}
        <div className="border rounded-lg p-6 bg-white ">
          <h2 className="text-lg font-semibold mb-4">Terms & Instructions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label htmlFor="addReceiverInstructions" className="block text-sm font-medium text-gray-700">
                Receiver Instructions
              </label>
              <textarea
                id="addReceiverInstructions"
                placeholder="Enter receiver instructions"
                value={formData.receiverInstructions}
                onChange={e => {
                  const { receiverInstructions, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, receiverInstructions: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.receiverInstructions ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.receiverInstructions && (
                <p className="mt-1 text-sm text-red-600">{formErrors.receiverInstructions}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="addReceiverHowToUseInstructions" className="block text-sm font-medium text-gray-700">
                Receiver How To Use Instructions
              </label>
              <textarea
                id="addReceiverHowToUseInstructions"
                placeholder="Enter receiver how to use instructions"
                value={formData.receiverHowToUseInstructions}
                onChange={e => {
                  const { receiverHowToUseInstructions, ...rest } = formErrors
                  setFormErrors(rest)
                  setFormData({ ...formData, receiverHowToUseInstructions: e.target.value })
                }}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md  focus:outline-none ${formErrors.receiverHowToUseInstructions ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              {formErrors.receiverHowToUseInstructions && (
                <p className="mt-1 text-sm text-red-600">{formErrors.receiverHowToUseInstructions}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6">
          <button
            type="submit"
            className="px-6 py-2 text-sm font-medium text-white bg-brand rounded-md hover:bg-brand/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
          >
            Add Program
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddPrograms
